import type { CSSProperties } from "react";
import { BehaviourIcon } from "./behaviour-icon";
import { SNAPSHOT_BEHAVIOURS, type SnapshotBehaviourMeta } from "./snapshot-tokens";

const CX = 200;
const CY = 200;
const RADIUS = 130;
const ORB_R = 30;
const VERTEX_R = 22;
const LEVELS = [0.35, 0.6, 0.85, 1];

const POSITIONS = SNAPSHOT_BEHAVIOURS.map((b, i) => {
  const angle = ((i * 72 - 90) * Math.PI) / 180;
  return {
    ...b,
    angle,
    vx: CX + Math.cos(angle) * RADIUS,
    vy: CY + Math.sin(angle) * RADIUS,
    sx: CX + Math.cos(angle) * (RADIUS * 0.62),
    sy: CY + Math.sin(angle) * (RADIUS * 0.62),
  };
});

const POLY_PATH =
  POSITIONS.map((p, i) => `${i === 0 ? "M" : "L"}${p.sx.toFixed(1)},${p.sy.toFixed(1)}`).join(" ") +
  " Z";

/** One placeholder per tab, so the row the tabs occupy does not jump. */
const SKELETON_TABS = ["64px", "44px", "62px", "48px", "40px", "56px"];

/* Each block carries the shared skeleton-sheen sweep, offset so one pass of
   light travels down the page instead of every block flashing in lockstep. */
function sheen(step: number): CSSProperties {
  return { "--skeleton-sheen-delay": `${step * 140}ms` } as CSSProperties;
}

const SENSE_LABELS: Record<SnapshotBehaviourMeta["id"], string> = {
  sleep: "Sensing sleep",
  food: "Sensing food",
  movement: "Sensing movement",
  stress: "Sensing stress",
  connection: "Sensing connection",
};

export function SnapshotSkeleton() {
  return (
    <div className="snap-sense-page pv-hero" aria-busy="true" aria-live="polite">
      <span className="sr-only">Sensing your week. Composing the snapshot.</span>

      <div className="pv-head skeleton-sheen max-w-[60ch]" style={sheen(0)}>
        <div className="snap-sense-eyebrow mb-3.5">
          <span className="bullet" />
          <span className="snap-sense-bar h-[10px] w-[180px] max-w-full" />
        </div>
        <div className="snap-sense-bar mb-3 h-[34px] w-[78%] max-w-full" />
        <div className="snap-sense-bar h-[14px] w-[92%] max-w-full" />
      </div>

      <nav className="snap-tabs skeleton-sheen" aria-hidden="true" style={sheen(1)}>
        {SKELETON_TABS.map((width) => (
          <span key={width} className="snap-sense-tab">
            <span className="snap-sense-bar h-[10px]" style={{ width }} />
          </span>
        ))}
      </nav>

      <section className="snap-hero">
        <div className="snap-hero-left skeleton-sheen" style={sheen(2)}>
          <div className="snap-sense-eyebrow">
            <span className="bullet" />
            <span className="snap-sense-bar h-[10px] w-[160px] max-w-full" />
          </div>
          <div className="snap-sense-bar h-[34px] w-[72%] max-w-full" />

          <div className="snap-hero-focus space-y-3">
            <span className="snap-sense-bar block h-[12px] w-[140px]" />
            <span className="snap-sense-bar block h-[40px] w-[40px] rounded-full" />
            <span className="snap-sense-bar block h-[12px] w-[70%] max-w-full" />
          </div>

          <div className="space-y-2">
            <span className="snap-sense-bar block h-[14px] w-[94%] max-w-full" />
            <span className="snap-sense-bar block h-[14px] w-[72%] max-w-full" />
            <span className="snap-sense-bar block h-[14px] w-[86%] max-w-full" />
          </div>
        </div>

        <div className="snap-hero-tree">
          <SensingRadar />
          <div className="snap-status-row">
            {POSITIONS.map((b, i) => (
              <span
                key={b.id}
                className="snap-sense-chip"
                style={{
                  ["--accent" as string]: b.color,
                  animationDelay: `${260 + i * 110}ms`,
                }}
              >
                <BehaviourIcon name={b.iconName} size={13} weight="duotone" />
                <span className="snap-sense-chip-label" style={{ color: b.color }}>
                  {SENSE_LABELS[b.id]}
                </span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="snap-section-head skeleton-sheen" style={sheen(3)}>
        <div className="snap-sense-eyebrow">
          <span className="bullet" />
          <span className="snap-sense-bar h-[10px] w-[220px] max-w-full" />
        </div>
        <span className="snap-sense-bar hidden h-[10px] w-[180px] sm:block" />
      </div>

      <div className="skeleton-sheen space-y-2" style={sheen(4)}>
        {POSITIONS.map((b) => (
          <SensingRow key={b.id} behaviour={b} />
        ))}
      </div>
    </div>
  );
}

function SensingRow({ behaviour }: { behaviour: SnapshotBehaviourMeta }) {
  return (
    <div
      className="snap-sense-row"
      style={{ ["--accent" as string]: behaviour.color }}
      aria-hidden="true"
    >
      <span
        className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-full"
        style={{
          background: `color-mix(in srgb, ${behaviour.color} 12%, transparent)`,
          color: behaviour.color,
        }}
      >
        <BehaviourIcon name={behaviour.iconName} size={16} weight="duotone" />
      </span>
      <div className="min-w-0 space-y-1.5">
        <span className="snap-sense-bar block h-[14px] w-[60%] max-w-full" />
        <span className="snap-sense-bar block h-[10px] w-[40%] max-w-full" />
      </div>
      <span className="snap-sense-bar hidden h-[22px] w-[40px] sm:block" />
      <span className="snap-sense-bar hidden h-[12px] w-[110px] sm:block" />
      <span className="snap-sense-row-status">
        <span className="dot" style={{ background: behaviour.color }} />
        {SENSE_LABELS[behaviour.id]}
      </span>
    </div>
  );
}

function SensingRadar() {
  return (
    <div className="snap-tree-wrap">
      <svg
        viewBox="0 0 400 400"
        preserveAspectRatio="xMidYMid meet"
        className="snap-tree-svg"
        role="img"
        aria-label="Plotting the radar"
      >
        <title>Plotting the radar</title>
        <defs>
          <radialGradient id="snap-sense-aura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--color-teal-200)" stopOpacity="0.5" />
            <stop offset="65%" stopColor="var(--color-teal-100)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--color-teal-100)" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="snap-sense-orb" cx="38%" cy="32%" r="72%">
            <stop offset="0%" stopColor="var(--color-surface-elevated)" stopOpacity="1" />
            <stop offset="65%" stopColor="var(--color-surface-elevated)" stopOpacity="0.92" />
            <stop offset="100%" stopColor="var(--color-teal-100)" stopOpacity="0.85" />
          </radialGradient>
        </defs>

        {[0, 1, 2].map((i) => (
          <circle
            key={i}
            className={`snap-sense-sonar${i === 1 ? " snap-sense-sonar-2" : ""}${i === 2 ? " snap-sense-sonar-3" : ""}`}
            cx={CX}
            cy={CY}
            r={ORB_R + 8}
            fill="none"
            stroke="var(--color-teal-400)"
            strokeOpacity="0.45"
            strokeWidth="1.4"
          />
        ))}

        {LEVELS.map((level) => {
          const pts = POSITIONS.map((p) => ({
            x: CX + Math.cos(p.angle) * RADIUS * level,
            y: CY + Math.sin(p.angle) * RADIUS * level,
          }));
          const d = `${pts.map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(" ")} Z`;
          return (
            <path
              key={level}
              d={d}
              fill="none"
              stroke="var(--color-mist-dark)"
              strokeOpacity={level === 1 ? 0.5 : 0.25}
              strokeWidth="1"
            />
          );
        })}

        {POSITIONS.map((p, i) => (
          <g key={`axis-${p.id}`}>
            <path
              id={`snap-sense-axis-${p.id}`}
              d={`M ${CX + Math.cos(p.angle) * ORB_R} ${CY + Math.sin(p.angle) * ORB_R} L ${p.vx.toFixed(1)} ${p.vy.toFixed(1)}`}
              fill="none"
              stroke="var(--color-mist-dark)"
              strokeOpacity="0.4"
              strokeWidth="1"
            />
            <circle r="2" fill={p.color} opacity="0.75">
              <animateMotion dur="5.6s" repeatCount="indefinite" begin={`${i * 1.1}s`}>
                <mpath href={`#snap-sense-axis-${p.id}`} />
              </animateMotion>
              <animate
                attributeName="opacity"
                values="0;0.7;0.7;0"
                keyTimes="0;0.18;0.82;1"
                dur="5.6s"
                begin={`${i * 1.1}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ))}

        <path
          d={POLY_PATH}
          className="snap-sense-poly-fill"
          fill="var(--color-teal-300)"
          fillOpacity="0"
        />
        <path
          d={POLY_PATH}
          className="snap-sense-poly"
          fill="none"
          stroke="var(--color-teal-600)"
          strokeOpacity="0.8"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />

        <g>
          <circle cx={CX} cy={CY} r={ORB_R * 1.7} fill="url(#snap-sense-aura)" />
          <circle
            cx={CX}
            cy={CY}
            r={ORB_R * 1.7}
            fill="url(#snap-sense-aura)"
            className="snap-orb-aura-breath"
            style={{ transformOrigin: `${CX}px ${CY}px` }}
          />
          <circle cx={CX} cy={CY} r={ORB_R} fill="url(#snap-sense-orb)" />
          <circle
            cx={CX}
            cy={CY}
            r={ORB_R}
            fill="none"
            stroke="var(--color-teal-300)"
            strokeWidth="1.4"
            strokeOpacity="0.7"
          />
          <g transform={`translate(${CX - 10}, ${CY - 11.5}) scale(0.072)`}>
            <g className="snap-orb-leaf-grp">
              <path
                className="snap-orb-leaf snap-orb-leaf-1"
                fill="var(--color-teal-leaf-pale)"
                d="M138.72 0C155.925 0 172.413 3.07018 187.66 8.68457V8.68945C127.19 56.8294 67.91 115.27 8.79004 185.02H8.78809C3.2377 169.858 6.44659e-05 154.736 0 140.44C0 62.8805 60.7899 0.000147746 138.72 0Z"
              />
              <path
                className="snap-orb-leaf snap-orb-leaf-2"
                fill="var(--color-teal-leaf-mid)"
                d="M249.807 53.835C268.614 77.6955 279.83 107.762 279.83 140.44C279.83 209.363 201.805 297.562 154.759 316.37C154.759 316.367 154.76 316.364 154.76 316.36C169.539 205.883 213.817 119.164 249.807 53.835Z"
              />
              <path
                className="snap-orb-leaf snap-orb-leaf-3"
                fill="var(--color-teal-400)"
                d="M8.79016 185.02C26.9102 234.54 69.7102 284.45 105.16 307.23C143.24 205.62 190.76 104.78 238.84 41.4899C224.51 27.1299 207.1 15.8399 187.66 8.68994C127.19 56.8299 67.9102 115.27 8.79016 185.02Z"
              />
              <path
                className="snap-orb-leaf snap-orb-leaf-4"
                fill="var(--color-teal-600)"
                d="M138.72 320C143.43 320 148.85 318.72 154.76 316.36C169.54 205.88 213.82 119.16 249.81 53.83C246.4 49.5 242.74 45.38 238.85 41.48C190.77 104.77 143.25 205.61 105.17 307.22C117.77 315.32 129.44 319.99 138.73 319.99L138.72 320Z"
              />
            </g>
          </g>
        </g>

        {POSITIONS.map((p, i) => (
          <g
            key={`v-${p.id}`}
            className="snap-sense-vertex"
            style={{
              transformOrigin: `${p.vx}px ${p.vy}px`,
              animationDelay: `${i * 0.9}s`,
            }}
          >
            <circle cx={p.vx} cy={p.vy} r={VERTEX_R + 4} fill={p.color} opacity="0.12" />
            <circle
              cx={p.vx}
              cy={p.vy}
              r={VERTEX_R}
              fill="var(--color-surface-elevated)"
              stroke={p.color}
              strokeWidth="2"
            />
            <foreignObject
              x={p.vx - VERTEX_R}
              y={p.vy - VERTEX_R}
              width={VERTEX_R * 2}
              height={VERTEX_R * 2}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: p.color,
                }}
              >
                <BehaviourIcon name={p.iconName} size={14} weight="duotone" />
              </div>
            </foreignObject>
          </g>
        ))}
      </svg>
    </div>
  );
}
